import { useState, useEffect } from 'react';
import { Archive, RefreshCw, Download } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { TimestampDisplay } from './TimestampDisplay';
import { formatBytes, timecode } from '../lib/utils';
import type { ApiService } from '../lib/api';

interface LogEntry {
  size?: number;
  time?: number | string;
}

interface LogsPanelProps {
  api: ApiService | null;
  uuid: string;
  sn: string;
}

export const LogsPanel = ({ api, uuid, sn }: LogsPanelProps) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadLogsMeta = async () => {
    if (!api || !uuid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getDeviceLogMeta(uuid);
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError((e as Error).message);
    }
    setLoading(false);
  };

  const downloadLog = async (index: number) => {
    if (!api || !uuid) return;
    setDownloading(index);
    try {
      const { url } = await api.getDeviceLog(uuid, index);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sn || uuid.slice(0,8)}-${timecode(new Date())}.zip`;
      a.click();
    } catch (e) {
      setError((e as Error).message);
    }
    setDownloading(null);
  };

  const requestLogs = async () => {
    if (!api || !uuid) return;
    setRequesting(true);
    try {
      await api.requestLogs(uuid);
    } catch (e) {
      setError((e as Error).message);
    }
    setRequesting(false);
  };

  useEffect(() => { loadLogsMeta(); }, [api, uuid]);

  return (
    <Card title="Device Logs" icon={Archive}>
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="secondary" onClick={loadLogsMeta} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </Button>
          <Button size="sm" variant="primary" onClick={requestLogs} disabled={requesting}>
            {requesting ? <RefreshCw size={14} className="animate-spin" /> : <Archive size={14} />} Request Logs
          </Button>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        {logs.length === 0 ? (
          <p className="text-gray-500 text-sm py-2">No logs available. Request logs from the device first.</p>
        ) : (
          <div className="space-y-2 overflow-auto">
            {logs.map((log, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-gray-700 rounded text-sm">
                <div>
                  <span className="text-white">Log #{i}</span>
                  <span className="text-gray-400 ml-3">{formatBytes(log.size)}</span>
                  <span className="text-gray-400 ml-3"> </span>
                  <TimestampDisplay value={log.time} className="text-md text-gray-400" />
                </div>
                <Button size="sm" variant="success" onClick={() => downloadLog(i)} disabled={downloading === i}>
                  {downloading === i ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
